import React, { useCallback, useEffect, useState } from 'react';
import { IconPencil, IconPlus, IconTrash, IconUser, IconUsersGroup } from '@tabler/icons-react';
import type { IntScorozvon } from '../../../../entities/int-scorozvon';
import {
  deleteIntScorozvon,
  fetchIntScorozvonList,
  fetchIntScorozvonMembers,
} from '../../../../entities/int-scorozvon';
import { pb } from '../../../../shared/api/pocketbase';
import { IntScorozvonModal } from '../../../../features/int-scorozvon-form';
import { Button } from '../../../../shared/ui/Button';

const IntScorozvonSection: React.FC = () => {
  const [records, setRecords] = useState<IntScorozvon[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; record?: IntScorozvon } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchIntScorozvonList()
      .then(setRecords)
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const members = await fetchIntScorozvonMembers(id);
      if (members.length > 0) {
        const batch = pb.createBatch();
        members.forEach(m => batch.collection('intScorozvonMembers').delete(m.id));
        await batch.send();
      }
      await deleteIntScorozvon(id);
      setConfirmDeleteId(null);
      load();
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const getManagerName = (record: IntScorozvon) => {
    if (record.expand?.managerId) {
      const u = record.expand.managerId;
      return u.name || u.email;
    }
    return record.managerId ? '...' : '—';
  };

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Интеграция Скорозвон</h2>
          <p className="adm-section-sub">
            {loading ? 'Загрузка...' : `${records.length} записей`}
          </p>
        </div>
        <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
          <IconPlus size={15} stroke={2.5} />Добавить
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <div className="adm-ut-wrap">
          <table className="adm-ut">
            <thead>
              <tr>
                <th>Название группы</th>
                <th>Менеджер</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && (
                <tr>
                  <td colSpan={3} className="adm-empty">Записи не найдены</td>
                </tr>
              )}
              {records.map(record => {
                const isConfirming = confirmDeleteId === record.id;
                return (
                  <tr key={record.id} className="adm-ut-row">
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconUsersGroup size={14} stroke={2} style={{ flexShrink: 0, opacity: 0.5 }} />
                        {record.groupName || '—'}
                      </span>
                    </td>
                    <td>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconUser size={14} stroke={2} style={{ flexShrink: 0, opacity: 0.5 }} />
                        {getManagerName(record)}
                      </span>
                    </td>
                      <td className="adm-ut-actions-cell">
                      {isConfirming ? (
                        <div className="adm-ut-confirm">
                          <Button variant="adm-cancel" size="sm" type="button" onClick={() => setConfirmDeleteId(null)}>
                            Отмена
                          </Button>
                          <Button variant="danger" size="sm" type="button" disabled={deleting} onClick={() => handleDelete(record.id)}>
                            {deleting ? '...' : 'Удалить'}
                          </Button>
                        </div>
                      ) : (
                        <div className="adm-ut-actions">
                          <Button
                            variant="adm-edit"
                            type="button"
                            title="Редактировать"
                            onClick={() => setModal({ mode: 'edit', record })}
                          >
                            <IconPencil size={13} stroke={2} />
                          </Button>
                          <Button
                            variant="adm-delete"
                            type="button"
                            title="Удалить"
                            onClick={() => setConfirmDeleteId(record.id)}
                          >
                            <IconTrash size={13} stroke={2} />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <IntScorozvonModal
          mode={modal.mode}
          initial={modal.record ?? null}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
};

export { IntScorozvonSection };
